import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { siteReviews, type SiteReview } from "@/lib/site-reviews";

const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then((m) => ({ default: m.QuoteForm })));
import { QuoteBand } from "@/components/site/QuoteBand";

const TITLE = "RESTORE® Hair Reviews — Real Patient Stories & Results";
const DESCRIPTION =
  "Read hair transplant reviews from real RESTORE® Hair patients. Firsthand experiences with our doctors, staff and No-Shave FUE results across the country.";

const PAGE_SIZE = 12;

export const Route = createFileRoute("/reviews")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://restorehair.com/reviews/" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/reviews/" }],
  }),
  component: ReviewsPage,
});

function Stars() {
  return (
    <div aria-label="5 out of 5 stars" className="text-lg tracking-widest text-primary">
      ★★★★★
    </div>
  );
}

const CLAMP = 420;

function splitReview(review: SiteReview): { short: string; more: string } {
  if (review.more && review.more.trim()) return { short: review.short, more: review.more };
  const full = review.short;
  if (full.length <= CLAMP) return { short: full, more: "" };
  let cut = full.indexOf(" ", CLAMP);
  if (cut === -1) cut = full.length;
  return { short: `${full.slice(0, cut).trim()}…`, more: full.slice(cut).trim() };
}

function ReviewCard({ review }: { review: SiteReview }) {
  const [open, setOpen] = useState(false);
  const { short, more } = splitReview(review);
  const hasMore = Boolean(more);


  return (
    <article className="mb-6 break-inside-avoid border border-border bg-background p-6 shadow-none">
      <Stars />
      <h2 className="mt-3 text-lg font-bold leading-snug text-foreground">{review.title}</h2>
      <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
        {short}
      </p>
      {hasMore && open && (
        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
          {more}
        </p>
      )}

      {hasMore && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-3 text-sm font-bold uppercase tracking-wide text-primary underline-offset-4 hover:underline"
        >
          {open ? "Read Less" : "Read More"}
        </button>
      )}
      <p className="mt-4 text-sm font-bold text-foreground">{review.name}</p>
    </article>
  );
}

function ReviewsPage() {
  const [visible, setVisible] = useState(PAGE_SIZE);
  const shown = siteReviews.slice(0, visible);

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="bg-muted py-14 md:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
            <h1 className="text-3xl font-extrabold uppercase tracking-tight text-foreground md:text-5xl">
              RESTORE® Hair Reviews
            </h1>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              At RESTORE® Hair, we're dedicated to providing exceptional patient care and delivering
              outstanding results. However, the true measure of our success lies in the feedback from
              our patients. Explore their firsthand experiences and see what they have to share below.
              Our hair transplant reviews and stories of transformation will show you what's possible
              when you choose RESTORE. You'll find real stories from real people that speak to our
              commitment to excellence.
            </p>
            <Link
              to="/results/"
              className="mt-8 inline-block border border-primary bg-primary px-8 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:bg-transparent hover:text-primary"
            >
              See All Before &amp; After Images
            </Link>
          </div>
        </section>
        <QuoteBand />

        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="columns-1 gap-6 md:columns-2 lg:columns-3">
              {shown.map((r, i) => (
                <ReviewCard key={`${r.name}-${i}`} review={r} />
              ))}
            </div>
            {visible < siteReviews.length && (
              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-block border border-primary px-10 py-3 text-sm font-bold uppercase tracking-wide text-primary transition hover:bg-primary hover:text-primary-foreground"
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </section>

        <Suspense fallback={null}>
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
